/** Theme adapter: Volt supplies the modal and carousel, Horizon supplies products and buttons. */
const PREFIX = 'lazy-oaf:returning-nudge:';
export function nudgeTiming(data = {}) {
  const read = (key, fallback, min, max) => {
    const value = data[key] === undefined || data[key] === '' ? NaN : Number(data[key]);
    return Number.isFinite(value) && value >= min && value <= max ? value : fallback;
  };
  return {
    delay: read('delay', 8, 0, 30),
    returnGap: read('returnGap', 30, 5, 120),
    repeatInterval: read('repeatInterval', 24, 1, 72),
    cooldown: read('cooldown', 24, 1, 72),
  };
}
export function recentProductIds(raw) {
  try {
    const values = JSON.parse(raw || '[]');
    return Array.isArray(values) ? [...new Set(values.filter(id => /^\d+$/.test(String(id))).map(String))].slice(0, 4) : [];
  } catch { return []; }
}
export function excludedRoute(path, root = '/') {
  const local = path.startsWith(root) ? path.slice(root.length) : path.replace(/^\//, '');
  return /^(?:cart|checkout|checkouts)(?:\/|$)/.test(local);
}
export function visitState(local, session, now = Date.now(), returnMinutes = 30) {
  const visitGap = returnMinutes * 60000;
  try {
    const prior = Number(local.getItem(PREFIX + 'visit'));
    let visit = local.getItem(PREFIX + 'visit-start');
    // Share the active visit across tabs; their sessionStorage is independent.
    if (!visit || !prior || now - prior >= visitGap) {
      visit = String(now);
      local.setItem(PREFIX + 'visit-start', visit);
      local.setItem(PREFIX + 'visit-returning', prior > 0 && now - prior >= visitGap ? 'yes' : 'no');
    }
    if (session.getItem(PREFIX + 'visit-start') !== visit) {
      session.setItem(PREFIX + 'visit-start', visit);
      session.removeItem(PREFIX + 'shown');
    }
    local.setItem(PREFIX + 'visit', String(now));
    return local.getItem(PREFIX + 'visit-returning') === 'yes';
  } catch { return false; }
}
export function markDisplayed(local, session, now = Date.now()) {
  local.setItem(PREFIX + 'shown-visit', local.getItem(PREFIX + 'visit-start'));
  local.setItem(PREFIX + 'shown', String(now));
  session.setItem(PREFIX + 'shown', 'yes');
}
export function canDisplay(local, session, cooldownHours, now = Date.now(), repeatHours = 24) {
  try {
    if (session.getItem(PREFIX + 'shown')) return false;
    const visit = local.getItem(PREFIX + 'visit-start');
    if (visit && local.getItem(PREFIX + 'shown-visit') === visit) return false;
    const dismissed = Number(local.getItem(PREFIX + 'dismissed'));
    const shown = Number(local.getItem(PREFIX + 'shown'));
    return (!dismissed || now - dismissed >= cooldownHours * 3600000) && (!shown || now - shown >= repeatHours * 3600000);
  } catch { return false; }
}
export function otherOverlayOpen(root, doc = document) {
  const input = doc.activeElement;
  if (input?.matches('input, textarea, select, [contenteditable="true"]')) return true;
  return [...doc.querySelectorAll('dialog[open], [role="dialog"], [aria-modal="true"], header details[open]')].some(el => {
    if (root.contains(el) || el.hidden || el.getAttribute('aria-hidden') === 'true') return false;
    const style = getComputedStyle(el);
    return style.visibility !== 'hidden' && style.display !== 'none' && el.getClientRects().length > 0;
  });
}
// Keep keyboard order aligned with the responsive design, using the same native links.
export function arrangeNudgeActions(actions, mobile) {
  const first = actions?.querySelector(mobile ? '.button--returning-checkout' : '.button-secondary--returning-basket');
  if (!first || actions.firstElementChild === first) return;
  const focused = actions.ownerDocument.activeElement;
  const restoreFocus = first.contains(focused);
  actions.prepend(first);
  if (restoreFocus) focused.focus({preventScroll: true});
}
const Base = globalThis.HTMLElement || class {};
export class ReturningNudge extends Base {
  #events;
  #local;
  #session;
  #request;
  #timer;
  #manual = false;
  #busy = false;
  #endAt;
  #timing;
  connectedCallback() {
    this.#events?.abort();
    this.#events = new AbortController();
    const {signal} = this.#events;
    const mobile = matchMedia('(max-width: 767px)');
    mobile.addEventListener('change', () => arrangeNudgeActions(this.querySelector('.volt-modal__actions'), mobile.matches), {signal});
    this.addEventListener('click', event => {
      if (event.target.closest('[data-nudge-editor-preview]')) this.openPreview();
    }, {signal});
    document.addEventListener('shopify:section:select', event => {
      if (event.detail?.sectionId === this.dataset.sectionId) this.openPreview();
    }, {signal});
    this.querySelector('dialog-component')?.addEventListener('dialog:close', () => {
      if (!this.#manual) { try { this.#local?.setItem(PREFIX + 'dismissed', String(Date.now())); } catch {} }
    }, {signal});
    const params = new URL(location.href).searchParams;
    const preview = globalThis.Shopify?.theme?.role === 'unpublished' ? params.get('nudge_preview') : null;
    if (excludedRoute(location.pathname, this.dataset.rootUrl)) return;
    if (['cart','recent'].includes(preview)) { this.openPreview(preview); return; }
    if (globalThis.Shopify?.designMode || this.dataset.enabled !== 'true') return;
    try { this.#local = localStorage; this.#session = sessionStorage; } catch { return; }
    this.#timing = nudgeTiming(this.dataset);
    if (!visitState(this.#local, this.#session, Date.now(), this.#timing.returnGap)) return;
    this.#endAt = Date.now() + 120000;
    this.#timer = setTimeout(() => this.#attempt(), this.#timing.delay * 1000);
  }
  disconnectedCallback() {
    this.#events?.abort(); this.#request?.abort(); clearTimeout(this.#timer);
    const modal = this.querySelector('dialog-component');
    if (modal?.querySelector('dialog[open]')) modal.closeDialog();
  }
  openPreview(mode = 'cart') {
    if (excludedRoute(location.pathname, this.dataset.rootUrl) || this.#busy) return;
    this.#manual = true; clearTimeout(this.#timer); this.#prepare(mode);
  }
  #attempt() {
    if (!this.isConnected || Date.now() > this.#endAt || !canDisplay(this.#local, this.#session, this.#timing.cooldown, Date.now(), this.#timing.repeatInterval)) return;
    if (document.visibilityState !== 'visible' || otherOverlayOpen(this)) {
      this.#timer = setTimeout(() => this.#attempt(), 1000); return;
    }
    this.#prepare();
  }
  async #fetchSection(url) {
    this.#request?.abort(); this.#request = new AbortController();
    const timeout = setTimeout(() => this.#request?.abort(), 6000);
    try {
      const response = await fetch(url, {signal: this.#request.signal, credentials: 'same-origin'});
      if (!response.ok) throw new Error('Nudge data unavailable');
      const root = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('lazy-oaf-returning-nudge');
      if (!root) throw new Error('Nudge section unavailable');
      return root;
    } finally { clearTimeout(timeout); }
  }
  async #prepare(previewMode) {
    if (this.#busy) return;
    this.#busy = true;
    try {
      const cartURL = new URL(this.dataset.cartUrl, location.origin);
      cartURL.searchParams.set('section_id', this.dataset.sectionId);
      let source = await this.#fetchSection(cartURL);
      let mode = 'cart'; let ids;
      if (Number(source.dataset.cartCount) === 0 || previewMode === 'recent') {
        try { ids = recentProductIds(localStorage.getItem('viewedProducts')); } catch { ids = []; }
        if (!ids.length) return;
        const searchURL = new URL(this.dataset.searchUrl, location.origin);
        searchURL.searchParams.set('section_id', this.dataset.sectionId);
        searchURL.searchParams.set('type', 'product');
        searchURL.searchParams.set('q', ids.map(id => 'id:' + id).join(' OR '));
        source = await this.#fetchSection(searchURL); mode = 'recent';
      }
      let slides = [...source.querySelectorAll('[data-media-carousel-slide]')];
      // S&D merchandising may include extra results; show only the stored product IDs, in visit order.
      if (ids) slides = ids.map(id => slides.find(el => el.dataset.productId === id)).filter(Boolean);
      slides = slides.slice(0, Math.max(1, Number(this.dataset.limit) || 8));
      if (!slides.length || !this.isConnected) return;
      if (!this.#manual && (document.visibilityState !== 'visible' || !canDisplay(this.#local, this.#session, this.#timing.cooldown, Date.now(), this.#timing.repeatInterval) || otherOverlayOpen(this))) {
        this.#timer = setTimeout(() => this.#attempt(), 1000); return;
      }
      const modal = this.querySelector('dialog-component');
      const copy = this.querySelector('[data-nudge-' + mode + '-copy]').content;
      modal.querySelector('.volt-modal__heading').textContent = copy.querySelector('h2').textContent;
      const paragraph = modal.querySelector('.volt-modal__paragraph');
      paragraph.replaceChildren(...copy.querySelector('div').cloneNode(true).childNodes);
      paragraph.hidden = !paragraph.textContent.trim() && !paragraph.querySelector('img');
      modal.querySelector('.volt-modal__actions').replaceChildren(this.querySelector('[data-nudge-' + mode + '-actions]').content.cloneNode(true));
      arrangeNudgeActions(modal.querySelector('.volt-modal__actions'), matchMedia('(max-width: 767px)').matches);
      const rail = modal.querySelector('.section-media-carousel');
      rail.querySelector('.mc-track').replaceChildren(...slides.map(el => el.cloneNode(true)));
      this.dataset.mode = mode;
      await customElements.whenDefined('dialog-component');
      if (!this.isConnected) return;
      if (!this.#manual && (document.visibilityState !== 'visible' || !canDisplay(this.#local, this.#session, this.#timing.cooldown, Date.now(), this.#timing.repeatInterval) || otherOverlayOpen(this))) {
        this.#timer = setTimeout(() => this.#attempt(), 1000); return;
      }
      if (!this.#manual) {
        markDisplayed(this.#local, this.#session);
      }
      modal.showDialog();
      requestAnimationFrame(() => requestAnimationFrame(() => {
        const carousel = globalThis.Alpine?.$data(rail);
        carousel?.refreshSlides(); carousel?.updateCarouselControls();
      }));
    } catch {
      // Missing data, denied storage and network failures leave the shopper uninterrupted.
    } finally { this.#busy = false; }
  }
}
if (globalThis.customElements && !customElements.get('lazy-oaf-returning-nudge')) customElements.define('lazy-oaf-returning-nudge', ReturningNudge);
